import { Employee } from '../../model/inko-backend.model'

/** Was ein Name braucht. Vor- und Nachname sind optional, damit auch schmale Attrappen passen. */
export type Namensquelle = Pick<Employee, 'email' | 'nickname'> & Partial<Pick<Employee, 'vorname' | 'nachname'>>

/**
 * Anzeigename einer Person: gepflegter Vor- und Nachname, sonst der Spitzname, sonst die Adresse.
 * Das Backend zieht den Spitznamen beim Speichern des Profils auf denselben Wert; die Reihenfolge
 * hier sorgt dafuer, dass ein spaeter von der Benutzerverwaltung geaenderter Spitzname den selbst
 * gepflegten Namen nicht verdraengt.
 */
export function mitarbeiterName(e: Namensquelle): string {
  const voll = [e.vorname, e.nachname]
    .map((teil) => teil?.trim() ?? '')
    .filter(Boolean)
    .join(' ')
  return voll || e.nickname?.trim() || e.email
}

/** Zwei Buchstaben für den runden Kreis: „Anna Keller“ → „AK“, „info@haus.de“ → „IN“. */
export function initialen(name: string): string {
  const teile = name
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
  if (teile.length === 0) return '?'
  if (teile.length === 1) return teile[0].slice(0, 2).toUpperCase()
  return (teile[0][0] + teile[teile.length - 1][0]).toUpperCase()
}

/** Sammelpostfaecher: daraus wird kein Namensvorschlag. */
const SAMMELADRESSEN = new Set([
  'info',
  'kontakt',
  'service',
  'empfang',
  'buero',
  'office',
  'team',
  'mail',
  'post',
  'verwaltung',
  'zentrale',
])

/**
 * Vorschlag fuer den Willkommensdialog. Gepflegte Werte gewinnen; sonst ein Spitzname mit
 * Leerzeichen (den hat jemand von Hand vergeben), sonst der Teil der Adresse vor dem @
 * („lena.brandt“ → Lena / Brandt). Ziffern, Sammelpostfaecher und Adressen mit Kuerzeln
 * („anna.v.d.berg“) ergeben keinen Vorschlag.
 */
export function namensVorschlag(e: Namensquelle): { vorname: string; nachname: string } {
  if (e.vorname?.trim() || e.nachname?.trim()) {
    return { vorname: e.vorname?.trim() ?? '', nachname: e.nachname?.trim() ?? '' }
  }
  const lokal = (e.email ?? '').replace(/@.*$/, '').toLowerCase()
  const spitzname = e.nickname?.trim() ?? ''
  const quelle = spitzname && spitzname.toLowerCase() !== lokal ? spitzname : lokal
  const teile = quelle
    .split(/[\s._-]+/)
    .filter(Boolean)
    .filter((teil) => !/\d/.test(teil))
  if (teile.length === 0 || (teile.length === 1 && SAMMELADRESSEN.has(teile[0].toLowerCase()))) {
    return { vorname: '', nachname: '' }
  }
  // Ein Teil mit hoechstens zwei Buchstaben ist in einer Adresse fast immer ein Kuerzel
  // („e.schmidt“, „anna.v.d.berg“, „lena.gr.brandt“). Am ersten PROD-Tag wurde der Vorschlag
  // meist unveraendert gespeichert und stand dann als „V D Berg“ im Verlauf; leer tippt man selbst.
  if (quelle === lokal && teile.some((teil) => teil.length <= 2)) {
    return { vorname: '', nachname: '' }
  }
  const gross = (teil: string) => teil.charAt(0).toUpperCase() + teil.slice(1)
  return { vorname: gross(teile[0]), nachname: teile.slice(1).map(gross).join(' ') }
}
